import { useGiftGuideHubLink } from '../../../theaWeb/hub/giftGuideHubFlag';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { shimmerFill } from '../../../motion';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, type Auth, type User } from 'firebase/auth';

import { useAuth } from '../../../theaWeb/firebase/FirebaseContext';
import { FreshFindFridaysCardSlot } from '../../../theaWeb/freshFind/FreshFindFridaysCard';
import { PersonTile } from '../dashboard/PersonTile';
import { AddPersonTile } from '../dashboard/AddPersonTile';
import { BoardsEmptyState } from '../dashboard/BoardsEmptyState';
import {
  useFriendPreviews,
  useFirestoreFriendPreviewLoader,
} from '../dashboard/useFriendPreviews';
import { useFriendsList } from '../dashboard/useFriendsList';
import type { AuthState, DashboardPerson, FriendPreviewLoader } from '../dashboard/types';
import { readHasBoardsHint } from '../../../theaWeb/lib/hasBoardsHint';
import { readPeopleCountHint, writePeopleCountHint } from '../../../theaWeb/lib/peopleCountHint';
import { PAGE_GUTTER_DESKTOP } from '../../../theaWeb/lib/pageGutter';

export interface BrowseMyFriendsSectionProps {
  /** Test/story override: skip the live listener and render this list. */
  peopleOverride?: DashboardPerson[];
  /** Test/story override: bypass Firestore and use this loader. */
  loaderOverride?: FriendPreviewLoader;
  /** Test/story override: simulate auth-resolution loading vs ready. */
  authOverride?: AuthState;
  /** Override the auth instance for tests. Defaults to the FirebaseProvider's auth. */
  authInstance?: Auth;
  /**
   * Called instead of `navigate('/board/:rid')` when a tile is clicked.
   * The route is owned by another in-flight PR; this prop lets stories
   * and tests assert without depending on the route existing.
   */
  onPersonClick?: (person: DashboardPerson) => void;
  /** Override heading copy. */
  heading?: string;
  /**
   * Opens sign-in for the named entry — LandingPage's existing door signature,
   * forwarded straight through to the Fresh Find Fridays Unlock pill (PR 4.5 /
   * THE-770). Absent → the pill is not rendered at all, because a door that
   * opens nothing is a decoration.
   */
  onSignInClick?: (entry: string) => void;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the hub gate is off. `LandingPage.tsx`
   * resolves the flag once and passes it down.
   */
  wideGutterEnabled?: boolean;
}

// Max-width matches Section (1400) so the header left-aligns with the
// tile grid below it on every breakpoint.
//
// THE ROW OWNS THE ALIGNMENT (THE-746 Phase 4, PR 4.4 / THE-769). The Fresh
// Find Fridays tag is a second child of this wrapper; at 768px and up it
// sits at the RIGHT end of the "Your people" line, a property of the row the
// two share, so it is declared here rather than inside the tag — a
// `margin-left: auto` or `align-self: flex-end` on the tag alone would be a
// child trying to describe a layout it cannot see.
//
// COLUMN BELOW 768px, ROW AT OR ABOVE IT (THE-873 follow-up, Kate
// 2026-10-07). The first pass put the flat tag beside the heading at every
// width; Kate's second look sent the phone tag to its own full-width line
// under "Your people · 34" instead (room for the "Sign in to unlock" line),
// so this wrapper stacks again below 768px — the SAME cutoff the tag's own
// copy swap and `HeadCount` below use, kept in step on purpose so the row
// never has two different breakpoints describing one layout.
// NO MAX-WIDTH, NO MARGIN:AUTO (THE-1161) — ONLY WHEN `$wideGutter` IS SET
// (giftGuideHubFlag.ts). See ComingUpStrip's Inner, which this wrapper
// shares a left-edge alignment with on every breakpoint; both moved
// together so the page's two stacked sections keep agreeing past 1568px
// wide, not just below it — and both read the same flag so they cannot
// disagree about which design is live either.
const HeadingWrap = styled.div<{ $wideGutter?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  ${({ $wideGutter }) => !$wideGutter && 'max-width: 1400px; margin: 0 auto;'}
  padding: 24px 16px 0;
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
  }
  @media (min-width: 1024px) {
    padding-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
    padding-right: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
  }
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
`;

// Section-header spec (header/IA spec v5 PR 3): sentence case, LEFT-
// aligned, one type register across every page top — 21px desktop /
// 19px mobile, 700, ink. The giant centered 28→52px display H1 is
// retired (no centered display H1s anywhere; Kate froze this 2026-07-10).
// Same spec as ComingUpStrip's "Coming up" banner header.
const Heading = styled.h2`
  margin: 0;
  text-align: left;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: hsl(var(--foreground));
  line-height: 1.2;
  @media (min-width: 768px) {
    font-size: 21px;
  }
`;

// Muted recipient count beside the header (15px per the v5 spec).
//
// BACK ON PHONE TOO (THE-873 follow-up, Kate 2026-10-07). The first pass
// dropped "· 34" below 768px because that width was spent sharing the line
// with the Fresh Find Fridays tag; now the tag moves to its own full-width
// line under the heading instead (HeadingWrap above), so the count no longer
// has to make room for it and renders at every width again.
const HeadCount = styled.span`
  margin-left: 8px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  color: hsl(var(--muted-foreground));
`;

const Section = styled.section<{ $wideGutter?: boolean }>`
  ${({ $wideGutter }) => !$wideGutter && 'max-width: 1400px; margin: 0 auto;'}
  padding: 14px 16px 48px;
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
  }
  @media (min-width: 1024px) {
    padding: 16px ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')} 64px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 32px;
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const SkeletonTile = styled.div`
  display: flex;
  flex-direction: column;
`;

const SkeletonFrame = styled.div`
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 16px;
  ${shimmerFill};
`;

// Mirrors TileLabel's flex row (PersonTile.tsx) tile-for-tile, including its
// responsive margin-top (8px below 640px, 12px at/above it — SkeletonLabel
// used to hard-code 12px, overshooting the real row by 4px on mobile). The
// row's height also used to be a flat 16px, 2px short of the real row:
// LabelEmoji's 18px/line-height:1 — not LabelName's 14-16px text — is what
// actually sets the real tile's height, so a plain CSS number drifts the
// moment either changes. SkeletonEmojiSpacer borrows that same emoji glyph
// (invisible) to size itself off whatever height THIS browser/OS renders it
// at, rather than a guessed constant — confirmed byte-for-byte against the
// real grid in Chromium at 390/768/1024/1440px (THE-1140 QA).
const SkeletonLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  @media (min-width: 640px) {
    margin-top: 12px;
  }
`;

const SkeletonEmojiSpacer = styled.span`
  font-size: 18px;
  line-height: 1;
  visibility: hidden;
`;

const SkeletonNameBar = styled.span`
  display: inline-block;
  width: 72px;
  height: 14px;
  border-radius: 4px;
  ${shimmerFill};
  @media (min-width: 640px) {
    width: 88px;
    height: 16px;
  }
`;

const EmptyState = styled.p`
  text-align: center;
  color: hsl(var(--muted-foreground));
  font-size: 15px;
  margin: 32px auto;
`;

/**
 * Resolve a Firebase `User` to the `AuthState` shape `useFriendPreviews`
 * expects. Anon users are surfaced as `signed-in` (with their anon uid)
 * so the boards-rebuild homepage can render their persisted recipients.
 * Originally anon was surfaced as `signed-out` which short-circuited the
 * Firestore subscription — fine pre-boards, since anon users had no
 * persistent recipient state, but wrong now: every quiz submission
 * creates a recipient under the anon uid that needs to render on the
 * homepage tile grid (boards-architecture proposal §3).
 *
 * The empty-state guard inside the component (people.length === 0)
 * keeps the section from rendering for fresh anon visitors — only
 * post-quiz anons see their tiles here.
 */
function userToAuthState(user: User | null, authReady: boolean): AuthState {
  if (!authReady) return { status: 'loading' };
  if (!user) {
    return { status: 'signed-out', onRequestSignIn: () => {} };
  }
  const initialSrc = user.displayName ?? user.email ?? '';
  const initial = (initialSrc.trim().charAt(0) || 'A').toUpperCase();
  return {
    status: 'signed-in',
    user: {
      uid: user.uid,
      displayName: user.displayName ?? undefined,
      initial,
    },
  };
}

/**
 * "Browse my friends" — the signed-in landing-page section. Subscribes
 * to the user's recipient subtree, lays them out 4-up on desktop / 2-up
 * on mobile, and shows a 2x2 collage of saved-item images per tile.
 *
 * No "Add someone" tile per Kate's V2 spec.
 */
export const BrowseMyFriendsSection: React.FC<BrowseMyFriendsSectionProps> = ({
  peopleOverride,
  loaderOverride,
  authOverride,
  authInstance: authInstanceProp,
  onPersonClick,
  heading = 'Your people',
  onSignInClick,
  wideGutterEnabled = false,
}) => {
  const ctxAuth = useAuth();
  const authInstance = authInstanceProp ?? ctxAuth;
  const navigate = useNavigate();
  const hubLink = useGiftGuideHubLink();

  // Subscribe to Firebase auth so we can branch on signed-in vs anon.
  const [user, setUser] = useState<User | null>(
    authOverride !== undefined ? null : authInstance.currentUser,
  );
  const [authReady, setAuthReady] = useState<boolean>(
    authOverride !== undefined ? true : !!authInstance.currentUser,
  );

  useEffect(() => {
    if (authOverride !== undefined) return;
    const unsub = onAuthStateChanged(authInstance, (next) => {
      setUser(next);
      setAuthReady(true);
    });
    return () => unsub();
  }, [authInstance, authOverride]);

  const authState: AuthState =
    authOverride ?? userToAuthState(user, authReady);

  const uid =
    authState.status === 'signed-in' ? authState.user.uid : null;

  // Real Firestore wiring (skipped if the caller provides overrides).
  const liveFriends = useFriendsList(peopleOverride ? null : uid);
  const liveLoader = useFirestoreFriendPreviewLoader(loaderOverride ? null : uid);

  const people: DashboardPerson[] = peopleOverride ?? liveFriends.friends;
  const friendsHydrated = peopleOverride ? true : liveFriends.hydrated;

  // useFriendPreviews requires a non-null loader. Provide a no-op when
  // there is no uid yet — the hook ignores it when `authState !== signed-in`.
  const noopLoader: FriendPreviewLoader = useMemo(
    () => ({ subscribe: () => () => {} }),
    [],
  );
  const loader: FriendPreviewLoader =
    loaderOverride ?? liveLoader ?? noopLoader;

  const { previews, resolved } = useFriendPreviews(authState, people, loader);

  // A PERMANENT (non-anon) signed-in user with no boards must never see a
  // blank body (spec v4). `userToAuthState` reports anon users as 'signed-in'
  // too, so the account kind comes from the live Firebase `user`, not
  // authState. When there is no live user (test/story overrides), or a
  // caller supplied `peopleOverride`, fall through to the legacy behavior so
  // anon-empty still reveals the marketing hero and stories render their list.
  const isPermanent = !!user && !user.isAnonymous;

  // THE-1104: persisted "did this uid have boards last time" hint (shared
  // with LandingPage's own layout gate, see `hasBoardsHint.ts`). An anon
  // visitor re-hydrates their Firestore subscription from scratch on every
  // mount, so `friendsHydrated` is briefly false even for someone with an
  // established grid — LandingPage already renders the boards layout (no
  // hero) for that cohort on this same hint, so this section staying null
  // through the same window reopened the jump the hint exists to prevent:
  // no grid, no placeholder, just a gap that the footer and everything below
  // fall into — then back out once the grid pops in. Read once at mount,
  // same as LandingPage — corrected against the live result below once it
  // settles, so a stale hint only costs one extra reveal, never a wrong one.
  const [hasBoardsHint] = useState(readHasBoardsHint);

  // THE-1140: last hydrated tile count, persisted the same way as the
  // boolean hint above. The loading skeleton below sizes itself off this
  // instead of a fixed count, so a returning visitor's page is already
  // close to its real (taller or shorter) height on first paint — a Back
  // navigation's restored scroll offset then lands on a page tall enough
  // to actually hold it, rather than snapping short and growing under the
  // user once the real grid hydrates.
  // `uid` is often already known on this component's very first render — a
  // cached Firebase session resolves `authInstance.currentUser` synchronously
  // — so the lazy initializer reads the hint off it directly instead of
  // unconditionally starting at 0 and correcting afterward: a correction
  // applied in an effect lands a frame after the skeleton's first paint
  // (QA caught this as an intermittent flash of the fixed-4 skeleton before
  // snapping to the remembered count on Back). When `uid` is NOT yet known
  // at mount (auth still resolving), this still starts at 0 and the
  // `useLayoutEffect` below corrects it pre-paint once `uid` arrives, same
  // as the lazy path would have — so a uid-mismatched (always-empty) read
  // is never permanently cached either way.
  const [peopleCountHint, setPeopleCountHint] = useState(() =>
    uid !== null ? readPeopleCountHint(uid) : 0,
  );
  const countHintReadForUid = useRef<string | null>(uid);
  useLayoutEffect(() => {
    if (uid === null || countHintReadForUid.current === uid) return;
    countHintReadForUid.current = uid;
    setPeopleCountHint(readPeopleCountHint(uid));
  }, [uid]);
  useEffect(() => {
    if (!friendsHydrated) return;
    writePeopleCountHint(uid, people.length);
  }, [friendsHydrated, people.length, uid]);

  // Whether this visitor is expected to have boards: a permanent account
  // always is (spec v4 — never a blank body), and an anon visitor is once
  // either the hint or an already-cached (post-success-error) result says so.
  // Everyone else is the genuine first-time anon this section must stay
  // quiet for until the first snapshot lands.
  const expectsBoards = isPermanent || hasBoardsHint || people.length > 0;

  // entry_point so the quiz_start funnel attributes this door — without
  // it, deriveEntryPoint resolved these to 'direct'. Shared by every
  // "Add someone" entry point on this page, including the loading-skeleton
  // tile (THE-1140), which renders the real AddPersonTile rather than a
  // shimmer placeholder for it.
  const goAddPerson = () =>
    navigate(hubLink('/quiz'), { state: { entry_point: 'add_person_tile' } });

  // Don't render the section while auth is still booting OR when the
  // user is fully signed-out (no Firebase user at all yet — rare since
  // ensureAuth() lazily creates an anon user on first need). Anon users
  // with an established uid pass through to the recipient list so their
  // boards render here.
  if (authState.status !== 'signed-in') return null;

  // These states derive from the canonical hook's atomic `status`, never from
  // `hydrated && !error` — so a loading or errored fetch can't render as empty
  // (the error-vs-failure guard). Plain loading (not yet timed out) falls
  // through to the skeleton below. Applies to anon visitors too whenever
  // `expectsBoards` — a returning board-haver's failed load must recover the
  // same way a permanent account's does, not vanish silently (THE-1104
  // follow-up): a load that didn't happen must never read as "no boards".
  if (expectsBoards && !peopleOverride) {
    // Only pre-empt with the error screen when there's nothing else to show.
    // The hook keeps prior `friends` on a post-success error, so a user who
    // HAD boards and hits a transient error keeps their (cached) grid rather
    // than blanking to "Couldn't load" — the error state is for the
    // nothing-loaded case.
    if (liveFriends.status === 'error' && people.length === 0) {
      return (
        <Section aria-label="Your people" $wideGutter={wideGutterEnabled}>
          <BoardsEmptyState variant="error" onRetry={liveFriends.retry} />
        </Section>
      );
    }
    if (liveFriends.status === 'loading' && liveFriends.timedOut) {
      return (
        <Section aria-label="Your people" $wideGutter={wideGutterEnabled}>
          <BoardsEmptyState variant="timeout" onRetry={liveFriends.retry} />
        </Section>
      );
    }
    // A confirmed-empty result only gets the explicit "no boards yet" card
    // for a permanent account. An anon visitor whose hint turned out to be
    // stale (or who has since emptied their boards) instead falls through to
    // the hydrated-empty guard below and renders nothing — LandingPage's own
    // layout gate will have already re-resolved to the marketing hero for
    // them by the same live count.
    if (isPermanent && liveFriends.status === 'success' && people.length === 0) {
      return (
        <Section aria-label="Your people" $wideGutter={wideGutterEnabled}>
          <BoardsEmptyState variant="empty" onAddPerson={goAddPerson} />
        </Section>
      );
    }
    // status === 'loading' && !timedOut (or anon success-empty) → fall
    // through to the skeleton/empty-collapse handling below.
  }

  // Genuine first-time anon visitor (no hint, nothing cached yet): stay null
  // rather than paint a skeleton this render only to tear it down the moment
  // the (likely-empty) first snapshot lands — the original THE-1104 jump.
  if (!isPermanent && !friendsHydrated && !expectsBoards) return null;

  // Hide the section entirely when there are no recipients. Render the
  // skeleton only briefly while we're waiting on the first Firestore
  // snapshot; once `friendsHydrated` lands and the list is empty, drop
  // the section so first-time anon visitors see the marketing hero
  // unbroken (QA spec: "No 'Your people' section visible — first-time
  // visit, no recipients yet"). Reached now only by anon-empty and the
  // override paths — permanent-empty is handled above.
  if (friendsHydrated && people.length === 0) return null;

  const handleClick = (person: DashboardPerson) => {
    if (onPersonClick) {
      onPersonClick(person);
      return;
    }
    navigate(hubLink(`/board/${person.id}`));
  };

  return (
    <>
      {/* The #your-people hash anchor used to live here — moved up to
          LandingPage's wrapper around ComingUpStrip + this section
          (Kate 2026-07-10: clicking "People" scrolled PAST the Coming
          Up strip, hiding exactly the thing worth seeing first). */}
      <HeadingWrap $wideGutter={wideGutterEnabled}>
        <Heading>
          {heading}
          {friendsHydrated && people.length > 0 && (
            <HeadCount aria-label={`${people.length} people`}>
              · {people.length}
            </HeadCount>
          )}
        </Heading>
        {/* Flag-gated: the slot renders nothing unless the Fresh Find Fridays
            gate resolves `enabled`, and nothing while it is still `pending` —
            so the default signed-in page is byte-identical to before this PR
            and never renders this line twice.

            `hasAccount` picks the variant (PR 4.5 / THE-770): an anonymous
            visitor gets the Unlock pill, a permanent account gets the plain
            card. It reads the LIVE Firebase user, the same source as the
            empty-state branch above, because `authState` reports anon users
            as 'signed-in' and would hand the Unlock cohort the signed-in card.
            Under a story/test `authOverride` there is no live user, so the
            variant resolves to no-account — the honest reading of a synthetic
            auth state with no account behind it, and moot in practice because
            the flag is off in both. */}
        <FreshFindFridaysCardSlot
          hasAccount={isPermanent}
          onSignInClick={onSignInClick}
        />
      </HeadingWrap>
      <Section aria-label="Browse my friends" $wideGutter={wideGutterEnabled}>
        {!friendsHydrated && people.length === 0 ? (
          <Grid data-testid="boards-skeleton">
            {/* The grid's first slot is always the real AddPersonTile, not a
                shimmer placeholder for it: that tile isn't data-dependent, so
                there is nothing to wait on, and no hand-tuned skeleton label
                CSS can be relied on to match its rendered height exactly (QA
                found a 3-6px drift between a mirrored label and the real
                "Add someone" text's browser-rendered line height — THE-1140).
                The remaining slots mirror the real per-person tile count:
                `peopleCountHint` when we have a remembered count from a prior
                hydration, falling back to 3 (for a total of 4) for a genuine
                first-time visitor with nothing on record yet. */}
            <AddPersonTile onClick={goAddPerson} />
            {Array.from({ length: peopleCountHint > 0 ? peopleCountHint : 3 }).map(
              (_, i) => (
                <SkeletonTile key={i}>
                  <SkeletonFrame />
                  <SkeletonLabel>
                    <SkeletonEmojiSpacer aria-hidden>🎁</SkeletonEmojiSpacer>
                    <SkeletonNameBar />
                  </SkeletonLabel>
                </SkeletonTile>
              ),
            )}
          </Grid>
        ) : people.length === 0 ? (
          <EmptyState>No one yet — find a gift to get started.</EmptyState>
        ) : (
          <Grid>
            <AddPersonTile onClick={goAddPerson} />
            {people.map((person) => (
              <PersonTile
                key={person.id}
                id={person.id}
                name={person.name}
                emoji={person.emoji}
                previewImages={previews[person.id] ?? []}
                loading={!resolved[person.id]}
                // Use href for native modifier-click (Cmd/Ctrl/Shift+Click
                // → new tab) when the consumer hasn't overridden the
                // destination via onPersonClick. Overridden case falls
                // back to button + onClick — the parent owns navigation.
                {...(onPersonClick
                  ? { onClick: () => handleClick(person) }
                  : { href: `/board/${person.id}` })}
              />
            ))}
          </Grid>
        )}
      </Section>
    </>
  );
};
